import React from "react";
import { Phone, Mail, MapPin, ExternalLink, ArrowUp } from "lucide-react";

const FN_LOGO =
  "https://customer-assets.emergentagent.com/job_premium-makhana-11/artifacts/4l1hs9ak_FN%20Logo%20%281%29.png";

const quickLinks = [
  { label: "Home", href: "#home" },
  { label: "Products", href: "#products" },
  { label: "About Us", href: "#about" },
  { label: "Value Add", href: "#value-add" },
  { label: "Distributor Enquiry", href: "#distributor" },
  { label: "Contact", href: "#contact" },
];

const grades = [
  { name: "4 Suta Grade", note: "≈ 12.68mm" },
  { name: "5 Suta Grade", note: "≈ 15.85mm" },
  { name: "6 Suta Grade", note: "≈ 19.02mm" },
  { name: "Mix Grade", note: "Multi-size" },
];

const Footer = () => {
  const scrollTo = (href) => {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-[#0A0A0A] border-t border-white/5" data-testid="footer">
      {/* Gold divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <button
              onClick={() => scrollTo("#home")}
              className="flex items-center gap-3 mb-5 group"
              data-testid="footer-brand"
            >
              <img
                src={FN_LOGO}
                alt="New Foxnourish Logo"
                className="w-14 h-14 rounded-full shadow-[0_0_20px_rgba(212,175,55,0.2)] group-hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all duration-300"
              />
              <div>
                <p
                  className="text-[#D4AF37] font-bold text-xl tracking-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  MakhanaG
                </p>
                <p className="text-[#525252] text-[10px] uppercase tracking-[3px]">
                  by New Foxnourish
                </p>
              </div>
            </button>

            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">
              Premium Bihar Makhana (Fox Nuts) sourced directly from the sacred
              lotus ponds of Mithila — pure, graded, and guaranteed.
            </p>

            <p
              className="text-[#D4AF37] text-sm italic mb-1"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              "Crafted by Nature. Perfected by Bihar."
            </p>
            <p
              className="text-[#D4AF37]/60 text-sm"
              style={{ fontFamily: "'Noto Sans Devanagari', sans-serif" }}
            >
              "प्रकृति की देन। बिहार की शान।"
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-[#FAFAFA] font-semibold text-xs uppercase tracking-[4px] mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => scrollTo(link.href)}
                    className="text-[#A1A1AA] text-sm hover:text-[#D4AF37] transition-colors duration-200 flex items-center gap-2 group"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span className="w-0 h-px bg-[#D4AF37] group-hover:w-3 transition-all duration-300" />
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Product Grades */}
          <div>
            <h4 className="text-[#FAFAFA] font-semibold text-xs uppercase tracking-[4px] mb-6">
              Our Grades
            </h4>
            <ul className="space-y-3">
              {grades.map((grade) => (
                <li key={grade.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                    <span className="text-[#A1A1AA] text-sm">{grade.name}</span>
                  </div>
                  <span className="text-[#525252] text-xs">{grade.note}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 p-4 bg-[#121212] rounded-xl border border-[#D4AF37]/15">
              <p className="text-[#D4AF37] text-xs font-semibold mb-1">
                New Foxnourish Pvt. Ltd.
              </p>
              <p className="text-[#525252] text-xs">GST Registered · CIN Compliant</p>
              <p className="text-[#525252] text-xs mt-1">Patna, Bihar — India</p>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-[#FAFAFA] font-semibold text-xs uppercase tracking-[4px] mb-6">
              Contact
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Phone size={13} className="text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <a
                    href="tel:+919502343899"
                    className="text-[#A1A1AA] text-sm hover:text-[#D4AF37] transition-colors font-medium"
                    data-testid="footer-phone"
                  >
                    +91-9502343899
                  </a>
                  <p className="text-[#525252] text-xs mt-0.5">Call or WhatsApp</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Mail size={13} className="text-[#D4AF37] mt-1 shrink-0" />
                <a
                  href="mailto:info@nexfoxnourish.in"
                  className="text-[#A1A1AA] text-sm hover:text-[#D4AF37] transition-colors break-all"
                  data-testid="footer-email"
                >
                  info@nexfoxnourish.in
                </a>
              </div>

              <div className="flex items-start gap-3">
                <MapPin size={13} className="text-[#D4AF37] mt-1 shrink-0" />
                <p className="text-[#A1A1AA] text-sm leading-relaxed">
                  Flat# 32, Om Vihar, Aparajita Apartment, Bhoothnath Road, Kankarbhag, Patna, Bihar
                </p>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/919502343899?text=Hi%2C%20I%27m%20interested%20in%20MakhanaG%20products"
              target="_blank"
              rel="noreferrer"
              className="mt-5 flex items-center gap-2 bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 hover:scale-[1.02]"
              data-testid="footer-whatsapp"
            >
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              WhatsApp Us Now
              <ExternalLink size={11} className="ml-auto" />
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-[#525252] text-xs text-center md:text-left">
            © 2025 New Foxnourish Private Limited. All rights reserved.{" "}
            <span className="text-[#D4AF37]/50">MakhanaG™</span>
          </p>
          <p className="text-[#525252] text-xs">Made with pride in Bihar, India</p>
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2 text-[#525252] hover:text-[#D4AF37] text-xs transition-colors group"
            data-testid="footer-scroll-top"
            aria-label="Back to top"
          >
            Back to top
            <ArrowUp
              size={12}
              className="group-hover:-translate-y-1 transition-transform"
            />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
